import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { COURSES } from "@/site";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadFormCompact() {
    const navigate = useNavigate();
    const [form, setForm] = React.useState({ name: "", phone: "", course: "" });
    const [busy, setBusy] = React.useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please share your name and phone.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: null,
                course: form.course || null,
                start_time: null,
                message: "Quick enquiry from home page",
            });
            navigate("/thank-you");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong. Please try WhatsApp.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section data-testid="home-quick-form" className="relative py-20 md:py-28 bg-background">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-[#1a1405] via-[#0f0c04] to-[#0a0a0a] p-8 md:p-12">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
                        <div className="lg:col-span-2">
                            <span className="inline-block text-[10px] tracking-[0.3em] uppercase text-gold border border-gold/30 rounded-full px-3 py-1">Limited Seats · January Batch</span>
                            <h2 className="mt-5 font-display text-2xl md:text-3xl text-white font-semibold leading-tight">
                                Quick enquiry. <span className="text-gold italic font-light">Zero commitment.</span>
                            </h2>
                            <p className="mt-3 text-sm text-white/55 leading-relaxed">
                                Drop your number and we will call within a few hours to schedule your free trial class at Ihao Music House Delhi.
                            </p>
                        </div>

                        <form onSubmit={submit} className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="qf-name" className="text-[10px] uppercase tracking-widest text-white/60">Name</Label>
                                <Input
                                    id="qf-name"
                                    required
                                    data-testid="qf-name"
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="Your full name"
                                    className="mt-1.5 bg-black/60 border-white/20 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold rounded-xl h-12"
                                />
                            </div>
                            <div>
                                <Label htmlFor="qf-phone" className="text-[10px] uppercase tracking-widest text-white/60">Phone</Label>
                                <Input
                                    id="qf-phone"
                                    required
                                    type="tel"
                                    data-testid="qf-phone"
                                    value={form.phone}
                                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                    placeholder="10-digit mobile"
                                    className="mt-1.5 bg-black/60 border-white/20 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold rounded-xl h-12"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-[10px] uppercase tracking-widest text-white/60">Course (optional)</Label>
                                <Select value={form.course} onValueChange={(v) => setForm((f) => ({ ...f, course: v }))}>
                                    <SelectTrigger data-testid="qf-course" className="mt-1.5 bg-black/60 border-white/20 text-white focus:ring-gold focus:border-gold rounded-xl h-12">
                                        <SelectValue placeholder="Which course interests you?" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                                        {COURSES.map((c) => (
                                            <SelectItem key={c.slug} value={c.name} className="focus:bg-gold/20">{c.name}</SelectItem>
                                        ))}
                                        <SelectItem value="Not sure yet" className="focus:bg-gold/20">Not sure yet</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="qf-submit"
                                className="md:col-span-2 mt-2 inline-flex items-center justify-center bg-gold text-black font-semibold text-sm px-8 py-4 rounded-full hover:bg-gold-hover hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 gold-glow"
                            >
                                {busy ? "Submitting…" : "Book Free Trial Class"}
                            </button>
                            <p className="md:col-span-2 text-[11px] text-white/40">By submitting, you agree to be contacted by Ihao Music House Delhi.</p>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
